/**
 * usePrivacyMode Hook + Context
 *
 * Manages privacy mode state for hiding sensitive financial information.
 * Uses React Context to ensure all components share the same state
 * and re-render together when privacy mode toggles.
 *
 * - Persists setting in localStorage
 * - Provides toggle function
 * - Instant updates across all components (no page refresh needed)
 * - Multi-tab sync via storage events
 */

import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

const PRIVACY_MODE_KEY = 'ninjacash_privacy_mode';

/**
 * Get initial privacy mode state from localStorage.
 */
function getInitialPrivacyMode(): boolean {
  try {
    const stored = localStorage.getItem(PRIVACY_MODE_KEY);
    return stored === 'true';
  } catch (err) {
    console.warn('[Privacy] Failed to read privacy mode from localStorage:', err);
    return false;
  }
}

/**
 * Save privacy mode state to localStorage.
 */
function savePrivacyMode(enabled: boolean): void {
  try {
    localStorage.setItem(PRIVACY_MODE_KEY, enabled.toString());
  } catch (err) {
    console.warn('[Privacy] Failed to save privacy mode to localStorage:', err);
  }
}

interface PrivacyModeContextValue {
  isPrivacyMode: boolean;
  togglePrivacyMode: () => void;
  setPrivacyMode: (enabled: boolean) => void;
}

// Create context with undefined default (will be provided by PrivacyModeProvider)
const PrivacyModeContext = createContext<PrivacyModeContextValue | undefined>(undefined);

/**
 * Provider component that wraps the app and provides shared privacy mode state.
 */
export function PrivacyModeProvider({ children }: { children: ReactNode }) {
  const [isPrivacyMode, setIsPrivacyModeState] = useState(() => getInitialPrivacyMode());

  // Listen for storage changes (for multi-tab sync)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === PRIVACY_MODE_KEY) {
        setIsPrivacyModeState(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const setPrivacyMode = useCallback((enabled: boolean) => {
    setIsPrivacyModeState(enabled);
    savePrivacyMode(enabled);
  }, []);

  const togglePrivacyMode = useCallback(() => {
    setIsPrivacyModeState((prev) => {
      const next = !prev;
      savePrivacyMode(next);
      return next;
    });
  }, []);

  return (
    <PrivacyModeContext.Provider value={{ isPrivacyMode, togglePrivacyMode, setPrivacyMode }}>
      {children}
    </PrivacyModeContext.Provider>
  );
}

/**
 * Hook for accessing privacy mode state.
 * Must be used within PrivacyModeProvider.
 */
export function usePrivacyMode(): PrivacyModeContextValue {
  const context = useContext(PrivacyModeContext);

  if (context === undefined) {
    throw new Error('usePrivacyMode must be used within a PrivacyModeProvider');
  }

  return context;
}
